import {useEffect, useState} from 'react';

/**
 * Cache em memória das mídias do totem (banners e logos).
 *
 * Cada arquivo é baixado uma vez e vira um blob: URL; a partir daí os <img> apontam para o blob
 * e não dependem mais da rede nem do cache HTTP (a produção entrega /totem/midia sem cache, e o
 * Wi-Fi do totem é fraco, então baixar o banner a cada volta do loop deixava a troca lenta).
 */

const carregadas = new Map<string, string>();
const pendentes = new Map<string, Promise<string>>();

/** Baixa a mídia (uma vez só) e devolve a URL do blob. Se falhar, devolve a própria URL. */
export function carregarMidia(url: string): Promise<string> {
	const pronta = carregadas.get(url);
	if (pronta) {
		return Promise.resolve(pronta);
	}
	const pendente = pendentes.get(url);
	if (pendente) {
		return pendente;
	}
	const promessa = fetch(url)
		.then(r => (r.ok ? r.blob() : Promise.reject(r.status)))
		.then(blob => {
			const blobUrl = URL.createObjectURL(blob);
			carregadas.set(url, blobUrl);
			return blobUrl;
		})
		.catch(() => url)
		.then(resultado => {
			pendentes.delete(url);
			return resultado;
		});
	pendentes.set(url, promessa);
	return promessa;
}

export function precarregarMidias(urls: Array<string | undefined>) {
	urls.forEach(url => {
		if (url) {
			carregarMidia(url);
		}
	});
}

/** URL pronta para usar no <img>: o blob, se já baixado; senão a original (e dispara o download). */
export function urlDaMidia(url: string): string {
	const pronta = carregadas.get(url);
	if (pronta) {
		return pronta;
	}
	carregarMidia(url);
	return url;
}

/** Versão em hook: re-renderiza quando o blob fica pronto. */
export function useMidia(url: string): string {
	const [atual, setAtual] = useState(() => carregadas.get(url) || url);
	useEffect(() => {
		let ativo = true;
		carregarMidia(url).then(resultado => {
			if (ativo) {
				setAtual(resultado);
			}
		});
		return () => {
			ativo = false;
		};
	}, [url]);
	return atual;
}

interface MidiaProps {
	src: string;
	alt: string;
	className?: string;
}

/** <img> que usa o cache em memória. */
export function Midia({src, alt, className}: MidiaProps) {
	const url = useMidia(src);
	return <img src={url} alt={alt} className={className}/>;
}
